import { SectionDescription } from "@/types/singularity";
import { pasteElements } from "../design-library/design-library";
import { TreeNode } from "@/types/tree";
import { documentStore, uiStore } from "@/store/store";
import { PromptInfo } from "../ai/types";
import { getSingularitySectionLibrary } from "./library-singleton";
import { getPromptInfoFromNode } from "./util";
import { copyNode, getBlankTree, moveNode } from "../tree/tree";

export const HEADER_SECTION_TYPE = "Headers";
export const FOOTER_SECTION_TYPE = "Footers";
export const EXCLUDED_SECTION_TYPES = ['Index', HEADER_SECTION_TYPE, FOOTER_SECTION_TYPE];

export async function populateDocumentFromPageDescription(
  sections: SectionDescription[]
) {
  for (const section of sections) {
    const sectionToInsert = getSectionToInsert(section);

    if (!sectionToInsert) continue;

    pasteElements([sectionToInsert], "inserting");
  }
}

export async function maybePopulateCurrentDocumentFromPageDescription() {
  if (!documentStore.document.documentMeta.singularityMeta) return;
  if (
    !documentStore.document.documentMeta.singularityMeta
      .singularityInitialSections
  )
    return;

  if (
    documentStore.document.documentMeta.singularityMeta &&
    documentStore.document.documentMeta.singularityMeta
      .initialSectionsAlreadyPopulated
  )
    return;

  await populateDocumentFromPageDescription(
    documentStore.document.documentMeta.singularityMeta
      .singularityInitialSections
  );

  markPageAsPopulated();
}

function markPageAsPopulated() {
  documentStore.setDocumentMeta({
    ...documentStore.document.documentMeta,
    singularityMeta: {
      ...documentStore.document.documentMeta.singularityMeta,
      initialSectionsAlreadyPopulated: true
    }
  });
}

export function getSectionToInsert(
  sectionDescription: SectionDescription
): TreeNode | null{
  const sectionLibrary = getSingularitySectionLibrary();

  const availableSections = sectionLibrary[sectionDescription.type];

  if (!availableSections || availableSections.length === 0) {
    console.log(
      `No sections available for type: ${sectionDescription.type} in library`
    );
    return null;
  }

  const randomIndex = Math.floor(Math.random() * availableSections.length);
  const randomSection = availableSections[randomIndex];

  const sectionWithPrompt = addPromptInfoToSection(
    randomSection,
    sectionDescription,
    randomIndex
  );
  const sectionWithFriendlyName = addFriendlyNameToSection(
    sectionWithPrompt,
    sectionDescription.sectionFriendlyName
  );

  return sectionWithFriendlyName;
}

export function getSectionsToInsert(sectionDescriptions: SectionDescription[]) {
  return sectionDescriptions.map(getSectionToInsert);
}

export function putSectionsInNewTree(sections: TreeNode[]) {
  const blankTree = getBlankTree();

  sections.forEach((section, index) => {
    const copiedNode = copyNode(blankTree, section);
    moveNode(blankTree, copiedNode.id, blankTree.root.id, index);
  });
}

export function addFriendlyNameToSection(
  sectionNode: TreeNode,
  sectionFriendlyName: string
) {
  // do i defensive copy this?

  sectionNode.data.properties = {
    ...(sectionNode.data.properties || {}),
    meta: {
      ...(sectionNode.data.properties?.meta || {}),
      friendlyName: sectionFriendlyName
    }
  };

  return sectionNode;
}

export function addPromptInfoToSection(
  sectionNode: TreeNode,
  sectionDescription: SectionDescription,
  index: number,
  shouldGenerateOnInitialLoad = true
) {
  const promptInfo: PromptInfo = {
    sectionDescription: sectionDescription.description,
    qualifiers: "",
    temperature: 1,
    lockedFields: [],
    locale: "en_US",
    unsplash: {
      enabled: false,
      fillEmptyImagesOnly: false,
      keywords: ""
    },
    singularity: {
      sectionData: {
        type: sectionDescription.type,
        index: index
      },
      shouldGenerateOnInitialLoad: shouldGenerateOnInitialLoad,
      unsplashKeywords: []
    },
    onboarding: {
      sectionType: sectionDescription.type,
      shouldGenerateOnInitialLoad: shouldGenerateOnInitialLoad,
    }
  };

  // do i defensive copy this?

  sectionNode.data.properties = {
    ...(sectionNode.data.properties || {}),
    meta: {
      ...(sectionNode.data.properties?.meta || {}),
      promptInfo: promptInfo
    }
  };

  return sectionNode;
}

export function getSectionTypes() {
  const allTypes = Object.keys(getSingularitySectionLibrary());
  return allTypes.filter((type) => (!EXCLUDED_SECTION_TYPES.includes(type)));
}

export function replaceSectionWithAnotherSimilarSection(element: TreeNode) {
  const promptInfo = getPromptInfoFromNode(element);

  if (!promptInfo.singularity.sectionData.type) return;

  const sectionToInsert = getSectionToInsert({
    type: promptInfo.singularity.sectionData.type,
    sectionFriendlyName: "unknown friendly name",
    description: promptInfo.sectionDescription
  });

  if (!sectionToInsert) return;

  uiStore.activateElement(element.id);
  pasteElements([sectionToInsert], "inserting");
  documentStore.deleteElement({ nodeId: element.id });
}

export function addAnotherSection(element: TreeNode) {
  const promptInfo = getPromptInfoFromNode(element);

  if (!promptInfo.singularity.sectionData.type) return;

  const sectionToInsert = getSectionToInsert({
    type: promptInfo.singularity.sectionData.type,
    sectionFriendlyName: "unknown friendly name",
    description: promptInfo.sectionDescription
  });

  if (!sectionToInsert) return;

  uiStore.activateElement(element.id);
  pasteElements([sectionToInsert], "inserting");
}
